import { useOptionalUser } from "~/providers/userProvider"
import type { Permissions } from "~/server/api/utils/roles"

/** The permission tier a control requires, mirroring the `Permissions` contract in
 * `~/server/api/utils/roles` and the tRPC procedure ladder:
 * `edit` → `editableProcedure`, `administer` → `adminProcedure`, `staff` → `staffProcedure`. */
export type PermissionRequirement = "edit" | "administer" | "staff"

type PermissionResult = { allowed: boolean; reason: string | undefined }

/** The `Permissions` flag each tier reads and the copy shown when it is missing. `satisfies`
 * ties every tier to a real flag, so a rename in `roles.ts` breaks the build instead of
 * silently gating nothing. */
const TIERS = {
  edit: { flag: "canEdit", reason: "You have read-only access" },
  administer: { flag: "canAdminister", reason: "You need admin access to change this" },
  staff: { flag: "isStaff", reason: "Only Hakio staff can change this" },
} as const satisfies Record<PermissionRequirement, { flag: keyof Permissions; reason: string }>

// One frozen instance per outcome, so results are referentially stable across renders.
const ALLOWED: PermissionResult = Object.freeze({ allowed: true, reason: undefined })
const BLOCKED: Record<PermissionRequirement, PermissionResult> = {
  edit: Object.freeze({ allowed: false, reason: TIERS.edit.reason }),
  administer: Object.freeze({ allowed: false, reason: TIERS.administer.reason }),
  staff: Object.freeze({ allowed: false, reason: TIERS.staff.reason }),
}

// Before the roles claim arrives an empty claim resolves to the default `user` tier: deny, but
// don't name a tier that isn't known yet.
const PENDING: PermissionResult = Object.freeze({ allowed: false, reason: undefined })

// Once per session: a missing provider is one wiring mistake, not one per gated control.
let warnedMissingProvider = false
function reportMissingProvider() {
  if (import.meta.env.DEV && !warnedMissingProvider) {
    warnedMissingProvider = true
    console.error(
      "usePermission: no UserProvider above this control, so `requires` allows everything. " +
        "Render it inside UserProvider (and TooltipProvider, which the gate's tooltip needs).",
    )
  }
}

/** The tier mapping itself, without React — the testable core of `usePermission`. */
export function evaluatePermission(
  permissions: Permissions,
  requires: PermissionRequirement,
): PermissionResult {
  return permissions[TIERS[requires].flag] ? ALLOWED : BLOCKED[requires]
}

/** Whether the current user meets `requires`, plus the copy explaining a denial. `reason` is
 * `undefined` when there is nothing to explain, so it goes straight into a `Tooltip` `title`.
 * `requires` is optional so primitives can call the hook unconditionally for an optional prop.
 *
 * Outside a `UserProvider` everything is allowed: gating is a UX affordance over the server's
 * enforcement, so a wiring bug must not lock anyone out of the surface around the control. */
export function usePermission(requires?: PermissionRequirement): PermissionResult {
  const user = useOptionalUser()
  if (requires === undefined) return ALLOWED
  if (user === undefined) {
    reportMissingProvider()
    return ALLOWED
  }
  if (user.isAuthLoading) return PENDING
  return evaluatePermission(user.permissions, requires)
}
